// @mui
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Checkbox,
  Grid,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { selectOwnerBookingDocuments } from 'src/utils/ownerBookingDocuments';
import { openVisitAttachment } from 'src/utils/openVisitAttachment';
import { medicationApi } from 'src/api/medication';
import {
  formatDosing,
  formatDuration,
} from 'src/sections/@dashboard/profile/components/medication-form/medicationDisplay';
import { reminderApi } from 'src/api/reminder';
import { treatmentApi } from 'src/api/treatment';
import { visitOwnerApi } from 'src/api/visit-owner';
import VetsiIcon from 'src/assets/icons/VetsiIcon';
import AiFeatureLabel from 'src/components/ai-feature-label/AiFeatureLabel';
import Iconify from 'src/components/iconify';
import {
  TreatmentPdfItem,
  TreatmentsSummaryPDF,
} from 'src/components/treatments-summary-pdf';
import { getFileIcon } from 'src/sections/@dashboard/profile/components/ProfileFilesTable';
import {
  GetVisitById,
  GetVisitByIdStatusEnum,
  VisitActivityResponseDtoActionEnum,
  VisitActivityResponseDtoTypeEnum,
} from 'woofwoof-api';
import VisitModalRemindersTab from './VisitModalRemindersTab';
import { StyledMarkdown } from './VisitModalNotesTabs';
import VisitTranscriptionContent from './VisitTranscriptionContent';
import { translateKey } from 'src/locales/translate';
import { VisitModalTab } from './visitModalTabEnum';
import { useClinicalQuery } from 'src/sections/@dashboard/visit/hooks/useClinicalQuery';


const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  [VisitActivityResponseDtoTypeEnum.Medication]: 'Lek',
  [VisitActivityResponseDtoTypeEnum.Treatment]: translateKey("visit.visitCourseMenuTreatment"),
  [VisitActivityResponseDtoTypeEnum.Reminder]: translateKey("visit.visitCourseMenuReminder"),
  [VisitActivityResponseDtoTypeEnum.Document]: translateKey("visit.visitCourseMenuDocument"),
  TRANSCRIPTION: 'Transkrypcja',
};

const ACTIVITY_ACTION_LABELS: Record<string, string> = {
  [VisitActivityResponseDtoActionEnum.Added]: 'Dodano',
  [VisitActivityResponseDtoActionEnum.Updated]: 'Zaktualizowano',
  [VisitActivityResponseDtoActionEnum.Removed]: translateKey("hardcoded.usunieto"),
  STARTED: 'Rozpoczęto',
  STOPPED: 'Zakończono',
  PAUSED: 'Wstrzymano',
  RESUMED: 'Wznowiono',
};


type VisitModalDetailsTabsProps = {
  visit: GetVisitById;
};

export default function VisitModalDetailsTabs({
  visit,
}: VisitModalDetailsTabsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const isVisitCompleted = visit.status === GetVisitByIdStatusEnum.Completed;

  // Always re-sign on open: the stored doc.url is a short-lived (60-min) signed
  // URL (and on some rows not a usable absolute URL at all), so opening it
  // directly either expires or resolves against the SPA origin and lands on the
  // dashboard. The per-entity endpoint re-signs from the source-of-truth filename.
  const openAttachment = (filename: string) =>
    openVisitAttachment({ visitId: visit.id, filename }).catch(() =>
      enqueueSnackbar(
        translateKey('hardcoded.nie_udalo_sie_otworzyc_dokumentu'),
        { variant: 'error' },
      ),
    );

  const hasNotes = !!(visit as any)?.notes;
  const [value, setValue] = useState(hasNotes ? VisitModalTab.Notes : VisitModalTab.Complaints);

  const profileId = (visit.profile as any)?.id || (visit.profile as any)?._id;

  // Owner-scoped activity endpoint: validates ownership via req.user.id so
  // the org-guarded variant isn't available here. Same response shape.
  const { data: activityData } = useQuery(
    ['visit-owner-activity-user-modal', visit.id],
    () => visitOwnerApi.getActivity(visit.id),
    { enabled: isVisitCompleted },
  );

  const activityItems = useMemo(
    () => activityData?.data || [],
    [activityData],
  );

  const hasMedicationActivity = useMemo(
    () =>
      activityItems.some(
        (a) => a.type === VisitActivityResponseDtoTypeEnum.Medication,
      ),
    [activityItems],
  );

  const hasTreatmentActivity = useMemo(
    () =>
      activityItems.some(
        (a) => a.type === VisitActivityResponseDtoTypeEnum.Treatment,
      ),
    [activityItems],
  );

  const addedMedicationIds = useMemo(
    () =>
      activityItems
        .filter(
          (a) =>
            a.type === VisitActivityResponseDtoTypeEnum.Medication &&
            a.action === VisitActivityResponseDtoActionEnum.Added,
        )
        .map((a) => a.referenceId),
    [activityItems],
  );

  const addedTreatmentIds = useMemo(
    () =>
      activityItems
        .filter(
          (a) =>
            a.type === VisitActivityResponseDtoTypeEnum.Treatment &&
            a.action === VisitActivityResponseDtoActionEnum.Added,
        )
        .map((a) => a.referenceId),
    [activityItems],
  );

  // User-scoped medication list: authorises via req.user.id so the owner can
  // only read their own profile's medications. The org-guarded variant is
  // rejected for non-staff callers.
  const { data: medicationsData } = useQuery(
    ['owner-profile-medications-user-modal', profileId],
    () =>
      medicationApi.medicationControllerGetProfileMedications({
        profileId: profileId || '',
      }),
    { enabled: isVisitCompleted && hasMedicationActivity && Boolean(profileId) },
  );

  // R1-1: the answer carries a selected clinic's materials, so the key is
  // that clinic's (the owner's scope when none is selected).
  const { data: treatmentsData } = useClinicalQuery(
    'profile-treatments-user-modal',
    profileId,
    [],
    (options) =>
      treatmentApi.treatmentRecordControllerGetProfileTreatments(
        { profileId: profileId || '' },
        options,
      ),
    { enabled: isVisitCompleted && hasTreatmentActivity && Boolean(profileId) },
  );

  const visitMedications = useMemo(
    () =>
      (medicationsData?.data.data || []).filter((m) =>
        addedMedicationIds.includes(m.id),
      ),
    [medicationsData, addedMedicationIds],
  );

  const visitTreatments = useMemo(
    () =>
      (treatmentsData?.data.data || []).filter((t) =>
        addedTreatmentIds.includes(t.id),
      ),
    [treatmentsData, addedTreatmentIds],
  );

  // Reminders bound to this visit. Gate the tab on `length > 0` so the
  // tab silently disappears when nothing is set, matching Leki/Zabiegi.
  const { data: remindersData } = useQuery(
    ['visit-reminders', visit.id],
    () => reminderApi.reminderControllerGetVisitReminders({ visitId: visit.id }),
    { enabled: isVisitCompleted },
  );
  const visitReminders = useMemo(
    () => remindersData?.data || [],
    [remindersData],
  );

  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState<Set<string>>(
    new Set(),
  );
  const [isExporting, setIsExporting] = useState(false);

  const handleToggleTreatment = (id: string) => {
    setSelectedTreatmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllTreatments = (checked: boolean) => {
    if (checked) {
      setSelectedTreatmentIds(new Set(visitTreatments.map((t) => t.id)));
    } else {
      setSelectedTreatmentIds(new Set());
    }
  };

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      const items: TreatmentPdfItem[] = visitTreatments
        .filter((t) => selectedTreatmentIds.has(t.id))
        .map((t) => ({
          name: t.name,
          treatmentDate: t.treatmentDate,
          priceNetto: t.priceNetto,
          vatRate: t.vatRate,
          priceBrutto: t.priceBrutto,
        }));

      const blob = await pdf(
        <TreatmentsSummaryPDF
          treatments={items}
          showPatientColumn={false}
          organizationName={(visit.organization as any)?.name || ''}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `zabiegi-wizyta-${format(
        new Date(visit.startTime),
        'dd-MM-yyyy',
      )}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar(translateKey("hardcoded.nie_udalo_sie_wygenerowac_pdf"), { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  // Visit-scoped documents come from the aggregator's $lookup on the
  // documents collection keyed by visitId. That's the single source of
  // truth — every write path (booking uploads, in-visit uploads via the
  // org endpoint, guest visit uploads) persists with `visitId` set.
  const visitDocuments = useMemo(() => {
    const docs = (((visit as any)?.documents as any[] | undefined) || []).slice();
    return docs.sort((a, b) => {
      const dateA = new Date(a.uploadedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.uploadedAt || b.createdAt || 0).getTime();
      return dateB - dateA;
    });
  }, [visit]);

  const transcription = (visit as any).transcription ?? null;
  const hasTranscription =
    Array.isArray(transcription?.segments) && transcription.segments.length > 0;

  const TABS = useMemo(
    () => [
      ...(hasNotes
        ? [
            {
              value: VisitModalTab.Notes,
              label: t('visit.visitNotesTab'),
              disabled: false,
            },
          ]
        : []),
      {
        value: VisitModalTab.Complaints,
        label: t('visit.complaintsHeading'),
        disabled: false,
      },
      ...(isVisitCompleted && activityItems.length > 0
        ? [
            {
              value: VisitModalTab.Course,
              label: translateKey("visit.visitCourseTitle"),
              disabled: false,
            },
          ]
        : []),
      ...(hasTranscription
        ? [
            {
              value: VisitModalTab.Transcription,
              label: (
                <AiFeatureLabel label={translateKey('visit.transcriptionTab')} />
              ),
              disabled: false,
            },
          ]
        : []),
      ...(isVisitCompleted && hasMedicationActivity
        ? [
            {
              value: VisitModalTab.Medications,
              label: translateKey("visit.tabMedications"),
              disabled: false,
            },
          ]
        : []),
      ...(isVisitCompleted && hasTreatmentActivity
        ? [
            {
              value: VisitModalTab.Treatments,
              label: translateKey("visit.tabTreatments"),
              disabled: false,
            },
          ]
        : []),
      ...(isVisitCompleted && visitReminders.length > 0
        ? [
            {
              value: VisitModalTab.Reminders,
              label: translateKey('visit.tabReminders'),
              disabled: false,
            },
          ]
        : []),
      ...(isVisitCompleted && visitDocuments.length > 0
        ? [
            {
              value: VisitModalTab.Documents,
              label: 'Dokumenty',
              disabled: false,
            },
          ]
        : []),
    ],
    [
      hasNotes,
      t,
      isVisitCompleted,
      activityItems.length,
      hasTranscription,
      hasMedicationActivity,
      hasTreatmentActivity,
      visitDocuments.length,
      visitReminders.length,
    ],
  );

  return (
    <Stack sx={{ gap: 2 }}>
      <Grid item xs={12}>
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderWidth: '1px',
            borderColor: theme.palette.divider,
            borderBottomStyle: 'solid',
            gap: 2,
            mx: { xs: 0, sm: 0, md: 2, lg: 0 },
          }}
        >
          <Tabs value={value} onChange={(_, newValue) => setValue(newValue)}>
            {TABS.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                disabled={tab.disabled}
                sx={{ fontSize: 16 }}
              />
            ))}
          </Tabs>
        </Stack>
      </Grid>
      <Grid item xs={12} sx={{ minHeight: 200 }}>
        {value === VisitModalTab.Notes && (
          <Stack sx={{ gap: 1, flex: 1, minHeight: 100 }}>
            <StyledMarkdown>{(visit as any).notes}</StyledMarkdown>
          </Stack>
        )}
        {value === VisitModalTab.Complaints && (
          <Stack
            sx={{
              gap: 2,
              height: 1,
            }}
          >
            <Stack sx={{ gap: 1, minHeight: 100 }}>
              <Typography>{visit.description}</Typography>
            </Stack>
            {selectOwnerBookingDocuments(visit?.documents as any[]).length >
              0 && (
              <Stack sx={{ gap: 1 }}>
                <Typography>{t('visit.attachmentsHeading')}</Typography>
                <Stack sx={{ gap: 2 }}>
                  {selectOwnerBookingDocuments(visit.documents as any[])
                    ?.sort((a, b) => {
                      const dateA = new Date(
                        a.uploadedAt || a.createdAt || 0,
                      ).getTime();
                      const dateB = new Date(
                        b.uploadedAt || b.createdAt || 0,
                      ).getTime();
                      return dateB - dateA;
                    })
                    .map((file, index) => (
                      <Stack
                        key={index}
                        sx={{
                          gap: 1.5,
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            padding: 1,
                            borderRadius: 1,
                            backgroundColor: theme.palette.background.neutral,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          {getFileIcon(file.originalFilename || file.filename)}
                        </Box>
                        <Typography
                          sx={{
                            '&:hover': {
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            },
                          }}
                          noWrap
                          onClick={() => openAttachment(file.filename)}
                        >
                          {file.originalFilename ||
                            file.filename.split('_').slice(2).join('_') ||
                            file.filename}
                        </Typography>
                      </Stack>
                    ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
        {value === VisitModalTab.Course && (
          <Stack sx={{ gap: 0 }}>
            {[...activityItems].reverse().map((item, index) => {
              const isLast = index === activityItems.length - 1;
              // On/off events (e.g. transcription) have no named item to append.
              const showName =
                item.action === VisitActivityResponseDtoActionEnum.Added ||
                item.action === VisitActivityResponseDtoActionEnum.Updated ||
                item.action === VisitActivityResponseDtoActionEnum.Removed;
              return (
                <Stack
                  key={item.id}
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    borderRadius: 1,
                    transition: 'background-color 0.15s',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Stack
                    sx={{
                      alignItems: 'center',
                      width: 28,
                      flexShrink: 0,
                      position: 'relative',
                      justifyContent: 'center',
                    }}
                  >
                    {index > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          width: 2,
                          top: 0,
                          bottom: '50%',
                          backgroundColor: theme.palette.divider,
                        }}
                      />
                    )}
                    {!isLast && (
                      <Box
                        sx={{
                          position: 'absolute',
                          width: 2,
                          top: '50%',
                          bottom: 0,
                          backgroundColor: theme.palette.divider,
                        }}
                      />
                    )}
                    <VetsiIcon
                      width={16}
                      height={16}
                      sx={{ flexShrink: 0, zIndex: 1 }}
                    />
                  </Stack>
                  <Stack sx={{ flex: 1, py: 1, ml: 1, minWidth: 0 }}>
                    <Stack
                      sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {ACTIVITY_TYPE_LABELS[item.type] || item.type}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {format(new Date(item.createdAt), 'HH:mm')}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {ACTIVITY_ACTION_LABELS[item.action] || item.action}
                      {showName ? `: ${item.name}` : ''}
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        )}
        {value === VisitModalTab.Transcription && (
          <Stack sx={{ minHeight: 100 }}>
            <VisitTranscriptionContent
              transcription={transcription}
              newestFirst={false}
              // Owner-facing modal: render from the owner's viewpoint so the
              // caregiver reads on the right in blue (WhatsApp-style), mirroring
              // the guest visit page. Staff-side modals keep the default.
              perspective="caregiver"
              professional={{
                name: `${visit?.professional?.firstName ?? ''} ${
                  visit?.professional?.lastName ?? ''
                }`.trim(),
                avatar: visit?.professional?.avatar,
              }}
              caregiver={{
                name: `${visit?.user?.firstName ?? ''} ${
                  visit?.user?.lastName ?? ''
                }`.trim(),
                avatar: visit?.user?.avatar,
              }}
            />
          </Stack>
        )}
        {value === VisitModalTab.Medications && (
          <Stack sx={{ gap: 1 }}>
            {visitMedications.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Brak lekarstw przypisanych podczas tej wizyty
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nazwa</TableCell>
                      <TableCell>{translateKey('medication.table.dosing')}</TableCell>
                      <TableCell>{translateKey('medication.table.duration')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visitMedications.map((med) => (
                      <TableRow key={med.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {med.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDosing(med)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDuration(med)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {med.notes || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        )}
        {value === VisitModalTab.Treatments && (
          <Stack sx={{ gap: 2 }}>
            {visitTreatments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{translateKey("hardcoded.brak_zabiegow_przypisanych_podczas_tej_wizyty")}</Typography>
            ) : (
              <>
                {selectedTreatmentIds.size > 0 && (
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Zaznaczono: {selectedTreatmentIds.size}
                    </Typography>
                    <LoadingButton
                      size="small"
                      variant="contained"
                      startIcon={
                        <Iconify icon="eva:download-outline" width={18} />
                      }
                      loading={isExporting}
                      onClick={handleExportPdf}
                      sx={{
                        background: theme.palette.common.black,
                        color: theme.palette.common.white,
                      }}
                    >
                      Generuj PDF
                    </LoadingButton>
                  </Stack>
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              selectedTreatmentIds.size ===
                                visitTreatments.length &&
                              visitTreatments.length > 0
                            }
                            indeterminate={
                              selectedTreatmentIds.size > 0 &&
                              selectedTreatmentIds.size <
                                visitTreatments.length
                            }
                            onChange={(e) =>
                              handleSelectAllTreatments(e.target.checked)
                            }
                          />
                        </TableCell>
                        <TableCell>Nazwa</TableCell>
                        <TableCell>{translateKey("visit.tableDate")}</TableCell>
                        <TableCell align="right">Cena brutto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visitTreatments.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedTreatmentIds.has(t.id)}
                              onChange={() => handleToggleTreatment(t.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {t.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {t.treatmentDate
                                ? format(
                                    new Date(t.treatmentDate),
                                    'dd.MM.yyyy',
                                  )
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={500}>
                              {t.priceBrutto != null
                                ? `${t.priceBrutto.toFixed(2)} ${translateKey("hardcoded.currencyPln")}`
                                : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Stack>
        )}
        {value === VisitModalTab.Documents && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nazwa pliku</TableCell>
                  <TableCell>{translateKey("search.ui.aboutTab")}</TableCell>
                  <TableCell>Data dodania</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visitDocuments.map((file) => {
                  const displayName =
                    file.originalFilename ||
                    file.filename?.split('_').slice(2).join('_') ||
                    file.filename;
                  const description = file.description;
                  return (
                    <TableRow
                      key={file.filename}
                      hover
                      onClick={() => openAttachment(file.filename)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack
                          sx={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          {getFileIcon(displayName)}
                          <Typography variant="body2" fontWeight={500}>
                            {displayName}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={description || ''}
                          arrow
                          disableHoverListener={
                            !description || description.length < 30
                          }
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {description || ''}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {file.uploadedAt
                            ? format(
                                new Date(file.uploadedAt),
                                'dd.MM.yyyy HH:mm',
                              )
                            : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {value === VisitModalTab.Reminders && (
          <VisitModalRemindersTab
            visitId={visit.id}
            profileId={profileId}
            mutationScope="owner"
          />
        )}
      </Grid>
    </Stack>
  );
}
